import ConsultModal from '@/components/ConsultModal';
import { GreenScreen } from '@/components/GreenScreen';
import { MilestoneLogo } from '@/components/icons/MilestoneLogo';
import { MilestoneLogoSmall } from '@/components/icons/MilestoneLogoSmall';
import { SavingsEstimateCard } from '@/components/SavingsEstimateCard';
import { ScheduleCallLink } from '@/components/ScheduleCallLink';
import { StepTitle } from '@/components/StepTitle';
import { defaultSavingEstimateContent } from '@/content/savingEstimateContent';
import { useFields } from '@/routes/hooks/useFields';
import { useLgBreakpoint } from '@/theme/hooks/useLgBreakpoint';
import { getBillSumationValues } from '@/utils/billSumation';
import { Flex, Space, Text, useMantineTheme } from '@mantine/core';
import { useParams } from '@tanstack/router';
import { useState } from 'react';
import { staticStepRoute } from '../router/router';

export const SavingEstimate = () => {
  const { stepId } = useParams({ from: staticStepRoute.id });
  const [opened, setOpened] = useState<boolean>(false);
  const fields = useFields();
  const isLgBreakpoint = useLgBreakpoint();
  const theme = useMantineTheme();

  const { totalBillAmount, lowerBound, upperBound } = getBillSumationValues(fields);
  const { title, subtitle } = defaultSavingEstimateContent;

  if (!stepId) throw new Error('StepId should be defined on SavingEstimate step');

  return (
    <Flex
      w="100%"
      wrap="wrap"
      justify="center"
      align="center"
      my="auto"
      rowGap={50}
      direction={isLgBreakpoint ? 'row' : 'column'}
      sx={{
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }}
    >
      <GreenScreen
        columnAlign="flex-start"
        stepId={stepId}
        primaryButtonLabel="Continue"
        primaryButtonProps={{
          style: {
            display: 'flex',
            minWidth: !isLgBreakpoint ? '100%' : 345,
            justifyContent: 'center',
            zIndex: 1,
          },
        }}
        elementAfterButton={
          <ScheduleCallLink
            onClick={() => setOpened(true)}
            label="Schedule a Call"
          />
        }
      >
        <Flex
          maw={610}
          direction="column"
          align="center"
          justify="center"
          style={{ zIndex: 1 }}
        >
          {!!title &&
            (typeof title === 'string' ? (
              <StepTitle
                isInverted
                ta="left"
                w="100%"
                size={34}
              >
                {title}
              </StepTitle>
            ) : (
              title()
            ))}
          {!!subtitle && (
            <Text
              ta="left"
              c="minor.1"
              size={16}
              mt={16}
            >
              {subtitle}
            </Text>
          )}
        </Flex>

        <Space h={{ base: theme.spacing.xl }} />

        {totalBillAmount > 0 && (
          <SavingsEstimateCard
            lowerBound={lowerBound}
            upperBound={upperBound}
            totalBillAmount={totalBillAmount}
          />
        )}

        <ConsultModal
          title="Schedule a consultation with a Resolve Advocate!"
          opened={opened}
          onClose={() => setOpened(false)}
          stepId={stepId}
          continueButtonType="submit"
          formName="blue-screen"
        />
      </GreenScreen>

      <Flex
        maw={595}
        miw={200}
        direction="column"
        align="center"
        justify="center"
        style={{ flex: 1 }}
      >
        <div
          style={{
            position: 'absolute',
            right: 0,
            bottom: 0,
          }}
        >
          {isLgBreakpoint ? (
            <MilestoneLogo
              style={{
                minWidth: '80vw',
                minHeight: '80vh',
                margin: '0 0 -5px 0',
              }}
            />
          ) : (
            <MilestoneLogoSmall
              style={{
                minWidth: '100vw',
              }}
            />
          )}
        </div>
      </Flex>
    </Flex>
  );
};
